import { useAuth, type ClientType } from "../auth/AuthContext";
import { cn } from "@/lib/utils";
import { InfoHint } from "./InfoHint";

const CLIENT_TYPE_META: Record<ClientType, { label: string; className: string; dot: string; hint?: string }> = {
  Sandbox: {
    label: "Sandbox",
    className: "border-accent-blue/25 bg-accent-blue/10 text-accent-blue",
    dot: "bg-accent-blue",
    hint: "Test mode: connections use the providers' test environments.",
  },
  Demo: {
    label: "Demo",
    className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    dot: "bg-amber-500",
    hint: "Connections are simulated and all data is made up.",
  },
  Live: {
    label: "Live",
    className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    dot: "bg-emerald-500",
  },
};

/** So nobody mistakes test data for real money. Renders nothing until the type is known rather than guessing. */
export function ClientTypePill() {
  const { activeOrganisationId, getClientType } = useAuth();
  const clientType = getClientType(activeOrganisationId);
  if (!clientType) return null;
  const meta = CLIENT_TYPE_META[clientType];

  return (
    <span
      data-testid="client-type-pill"
      aria-label={`${meta.label} environment`}
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold uppercase tracking-wide",
        meta.className,
      )}
    >
      <span aria-hidden className={cn("size-1.5 rounded-full", meta.dot)} />
      {meta.label}
      {meta.hint ? (
        <InfoHint label={`${meta.label} environment`} side="bottom" className="text-current opacity-70 hover:text-current">
          {meta.hint}
        </InfoHint>
      ) : null}
    </span>
  );
}
